export const QA_BUSINESS_CONTEXTS = Object.freeze({
  FOR_RENT: 'RENT',
  FOR_SALE: 'SALE',
});

export const QA_REFERENCE_ROUTE_PATHS = Object.freeze({
  BASE_PATH: '/',
  HOME: '/v2/home',
});

export const QA_REFERENCE_ROUTE_FEATURE_FLAGS = Object.freeze({
  NEW_MENU_HOME_ITEM: Symbol('NEW_MENU_HOME_ITEM'),
});

export const getSerializedFeatureFlagKey = featureFlag =>
  typeof featureFlag === 'symbol' ? featureFlag.description : featureFlag;

export const QA_REFERENCE_ROUTE_BINDINGS = Object.freeze({
  IDENTITY_WEBAPP_URL: 'IDENTITY_WEBAPP_URL',
});

const getBrowserWindow = () => (typeof window === 'undefined' ? {} : window);

export const getQAReferenceIdentityWebappUrl = () =>
  getBrowserWindow().globalConfig?.[
    QA_REFERENCE_ROUTE_BINDINGS.IDENTITY_WEBAPP_URL
  ];

const missingIdentityAdapter = () => {
  throw new Error('QA reference identity adapter is not configured');
};

const defaultRiskAssessment = Object.freeze({
  clearAccountId: () => getBrowserWindow().RiskAssessment?.clearAccountId?.(),
  setAccountId: () => getBrowserWindow().RiskAssessment?.setAccountId?.(),
});

const defaultQAReferenceRouteAdapters = Object.freeze({
  hasAuthCookie: () => false,
  getLoginUrlWithRedirect: missingIdentityAdapter,
  getIdentityLogoutUrl: missingIdentityAdapter,
  RiskAssessment: defaultRiskAssessment,
  getFeatureFlagEnabled: missingIdentityAdapter,
  captureMessage: () => undefined,
});

let qaReferenceRouteAdapters = defaultQAReferenceRouteAdapters;
let qaReferenceRouteFeatureFlagProvider;

export const configureQAReferenceRouteFeatureFlagProvider = provider => {
  qaReferenceRouteFeatureFlagProvider = provider;
};

export const getQAReferenceRouteFeatureFlagEnabled = featureFlag => {
  if (featureFlag !== QA_REFERENCE_ROUTE_FEATURE_FLAGS.NEW_MENU_HOME_ITEM) {
    return false;
  }

  return qaReferenceRouteFeatureFlagProvider(
    getSerializedFeatureFlagKey(featureFlag)
  );
};

export const configureQAReferenceRouteAdapters = adapters => {
  qaReferenceRouteAdapters = Object.freeze({
    ...defaultQAReferenceRouteAdapters,
    ...adapters,
  });
};

export const getQAReferenceRouteAdapters = () => qaReferenceRouteAdapters;

export const QA_PROPERTY_OFFER_CONTEXT_BY_REFERENCE = Object.freeze({
  [QA_BUSINESS_CONTEXTS.FOR_RENT]: 'rental',
  [QA_BUSINESS_CONTEXTS.FOR_SALE]: 'purchase',
});

export const QA_FINALIDADE_BY_REFERENCE_CONTEXT = Object.freeze({
  [QA_BUSINESS_CONTEXTS.FOR_RENT]: 'aluguel',
  [QA_BUSINESS_CONTEXTS.FOR_SALE]: 'venda',
});

const BUSINESS_CONTEXT_ALIASES = Object.freeze({
  rent: QA_BUSINESS_CONTEXTS.FOR_RENT,
  rental: QA_BUSINESS_CONTEXTS.FOR_RENT,
  aluguel: QA_BUSINESS_CONTEXTS.FOR_RENT,
  locacao: QA_BUSINESS_CONTEXTS.FOR_RENT,
  locação: QA_BUSINESS_CONTEXTS.FOR_RENT,
  for_rent: QA_BUSINESS_CONTEXTS.FOR_RENT,
  sale: QA_BUSINESS_CONTEXTS.FOR_SALE,
  sales: QA_BUSINESS_CONTEXTS.FOR_SALE,
  purchase: QA_BUSINESS_CONTEXTS.FOR_SALE,
  venda: QA_BUSINESS_CONTEXTS.FOR_SALE,
  comprar: QA_BUSINESS_CONTEXTS.FOR_SALE,
  compra: QA_BUSINESS_CONTEXTS.FOR_SALE,
  for_sale: QA_BUSINESS_CONTEXTS.FOR_SALE,
});

export const QA_HOUSE_TYPE_BY_REFERENCE = Object.freeze({
  Casa: 'casa',
  Apartamento: 'apartamento',
  CasaCondominio: 'casa_condominio',
  StudioOuKitchenette: 'studio',
});

const HOUSE_TYPE_ALIASES = Object.freeze({
  casa: 'casa',
  house: 'casa',
  home: 'casa',
  apartamento: 'apartamento',
  apartment: 'apartamento',
  residential: 'apartamento',
  empreendimento: 'apartamento',
  casacondominio: 'casa_condominio',
  casa_condominio: 'casa_condominio',
  casa_condominio_: 'casa_condominio',
  townhouse: 'casa_condominio',
  condominium: 'casa_condominio',
  studiooukitchenette: 'studio',
  studio_ou_kitchenette: 'studio',
  studio: 'studio',
  kitchenette: 'studio',
  kitnet: 'studio',
  penthouse: 'cobertura',
  cobertura: 'cobertura',
  land: 'terreno',
  terreno: 'terreno',
  commercial: 'comercial',
  commercialproperty: 'comercial',
  comercial: 'comercial',
  office: 'sala_comercial',
  sala_comercial: 'sala_comercial',
});

export const QA_AMENITY_TYPE_BY_REFERENCE = Object.freeze({
  area: 'AREA',
  bedroom: 'BEDROOM',
  bathroom: 'BATHROOM',
  parking: 'PARKING',
  floor: 'FLOOR',
  pets: 'PETS',
  furniture: 'FURNITURE',
  subway: 'SUBWAY',
});

export const QA_CONDOMINIUM_TYPES = Object.freeze({
  Normal: 'normal',
  NaoInformado: 'nao_informado',
  NaoExiste: 'nao_existe',
  IncluidoNoAluguel: 'incluido_no_aluguel',
});

export const QA_CONDOMINIUM_TYPE_LABELS = Object.freeze({
  normal: 'Condomínio',
  nao_informado: 'Condomínio não informado',
  nao_existe: 'Não existe condomínio',
  incluido_no_aluguel: 'Condomínio incluso no aluguel',
});

export const QA_IPTU_TYPES = Object.freeze({
  Normal: 'normal',
  NaoInformado: 'nao_informado',
  NaoExiste: 'nao_existe',
  IncluidoNoAluguel: 'incluido_no_aluguel',
});

export const QA_IPTU_TYPE_LABELS = Object.freeze({
  normal: 'IPTU',
  nao_informado: 'IPTU não informado',
  nao_existe: 'Não existe IPTU',
  incluido_no_aluguel: 'IPTU incluso no aluguel',
});

export const QA_RENTAL_ADMINISTRATOR_BY_REFERENCE = Object.freeze({
  OWNER: 'owner',
  QUINTOANDAR: 'imobiliaria',
  THIRD_PARTY: 'terceiro',
});

export const QA_HOUSE_STATUS_BY_REFERENCE = Object.freeze({
  aguardando_publicacao: Object.freeze({
    status: 'disponivel',
    listingStatus: 'edicao',
  }),
  edicao: Object.freeze({ status: 'disponivel', listingStatus: 'edicao' }),
  edicaosemusuario: Object.freeze({
    status: 'disponivel',
    listingStatus: 'edicao',
  }),
  edicaousuarionaoconfirmado: Object.freeze({
    status: 'disponivel',
    listingStatus: 'edicao',
  }),
  publicado: Object.freeze({
    status: 'disponivel',
    listingStatus: 'publicado',
  }),
  anunciado: Object.freeze({
    status: 'disponivel',
    listingStatus: 'publicado',
  }),
  despublicado: Object.freeze({
    status: 'inativo',
    listingStatus: 'despublicado',
  }),
  suspenso: Object.freeze({ status: 'inativo', listingStatus: 'suspenso' }),
  opted_out: Object.freeze({ status: 'inativo', listingStatus: 'opted_out' }),
  excluido: Object.freeze({
    status: 'inativo',
    listingStatus: 'despublicado',
  }),
  alugado: Object.freeze({ status: 'alugado', listingStatus: 'despublicado' }),
  vendido: Object.freeze({ status: 'vendido', listingStatus: 'despublicado' }),
  reservado: Object.freeze({
    status: 'reservado',
    listingStatus: 'publicado',
  }),
});

export const QA_HOUSE_ACTIVE_STATUSES = Object.freeze([
  'aguardando_publicacao',
  'edicao',
  'edicaoSemUsuario',
  'edicaoUsuarioNaoConfirmado',
  'publicado',
]);

export const QA_HOUSE_OCCUPANCY_BY_REFERENCE = Object.freeze({
  EMPTY: 'vazio',
  NONE: 'vazio',
  OWNER: 'proprietario',
  RENTED: 'inquilino',
  RENTED_OTHER: 'inquilino_terceiro',
});

const HOUSE_OCCUPANCY_ALIASES = Object.freeze({
  empty: 'vazio',
  none: 'vazio',
  vazio: 'vazio',
  owner: 'proprietario',
  proprietario: 'proprietario',
  rented: 'inquilino',
  tenant: 'inquilino',
  tenant_living: 'inquilino',
  inquilino: 'inquilino',
  rented_other: 'inquilino_terceiro',
  inquilino_terceiro: 'inquilino_terceiro',
});

export const QA_FEATURE_FLAG_MAP = Object.freeze({
  MIGRATION_TO_API_V3: Object.freeze({
    status: 'not_applicable',
    reason: 'api_v3_migration_not_used',
  }),
  VISIT_DONE_V3: Object.freeze({
    status: 'always_on',
    canonical: 'visit.done',
  }),
  REGISTER_VISIT_V3: Object.freeze({
    status: 'always_on',
    canonical: 'visit.registered',
  }),
  VISIT_CONFIRMATION_BY_AGENT: Object.freeze({
    status: 'runtime_toggle',
    toggle: 'lais_visit_request_enabled',
  }),
  VISIT_AUTOMATIC_CANCELLATION: Object.freeze({
    status: 'runtime_toggle',
    toggle: 'lais_visit_automatic_cancellation_enabled',
  }),
  RISK_ASSESSMENT_ENABLED: Object.freeze({
    status: 'runtime_toggle',
    toggle: 'lais_risk_assessment_enabled',
  }),
  LISTING_BUSINESS_CONTEXT_FEATURE_FLAG: Object.freeze({
    status: 'always_on',
    canonical: 'listing_business_context',
  }),
});

export const QA_TENANT_FEATURES = Object.freeze([
  'RESERVATION_ALLOWED',
  'PP_CR_CHAT',
  'LR_CR_CHAT',
  'TRAZ_QUEM_COMPRA',
  'DIRECT_REFERRAL',
  'TENANT_SERVICE_FEE',
  'HOME_OWNER_INSURANCE',
  'SELF_SERVICE_TAXES',
  'INTERCOM',
]);

export const QA_REFERENCE_EVENT_TYPES = Object.freeze({
  schedule: 'visit.scheduled',
  register: 'visit.registered',
});

export const QA_TRACKING_EVENT_MAP = Object.freeze({
  ...QA_REFERENCE_EVENT_TYPES,
  visitScheduled: 'visit.scheduled',
  visitConfirmed: 'visit.confirmed',
  visitCanceled: 'visit.canceled',
  visitDone: 'visit.done',
  offerSent: 'offer.sent',
  offerAccepted: 'offer.accepted',
  offerDeclined: 'offer.declined',
  offerWithdrawn: 'offer.withdrawn',
  contractSent: 'contract.sent',
  contractSigned: 'contract.signed',
  documentationStatusChanged: 'documentation.status_changed',
  negotiationStatusChanged: 'negotiation.status_changed',
});

export const QA_CANCELLATION_REASON_BY_REFERENCE = Object.freeze({
  PERSON_CANNOT_ATTEND: 'person_cannot_attend',
  PERSON_NOT_INTERESTED: 'person_not_interested',
  PERSON_SCHEDULED_FOR_ANOTHER_TIME: 'person_scheduled_for_another_time',
  PERSON_DOESNT_WANT: 'person_doesnt_want',
  PERSON_HAD_ISSUES_WITH_AGENT: 'person_had_issues_with_agent',
  PERSON_IDENTIFIED_LISTING_AS_INACCURATE:
    'person_identified_listing_as_inaccurate',
  VISIT_OCCURRED_AT_A_DIFFERENT_TIME: 'visit_occurred_at_a_different_time',
  CANNOT_CONTACT_ONE_OF_THE_PARTIES: 'cannot_contact_one_of_the_parties',
  PROPERTY_TEMPORARILY_UNAVAILABLE: 'property_temporarily_unavailable',
  PROPERTY_INDEFINITELY_UNAVAILABLE: 'property_indefinitely_unavailable',
  PROPERTY_ON_HOLD_FOR_ANOTHER_PROSPECT:
    'property_on_hold_for_another_prospect',
  PROPERTY_RESERVED: 'property_reserved',
  CONSEQUENCE_MANAGEMENT: 'consequence_management',
  REQUEST_EXPIRED: 'request_expired',
});

export const QA_REFUSAL_REASON_BY_REFERENCE = Object.freeze({
  OwnerSoldHouse: 'OwnerSoldHouse',
  OwnerSoldApartment: 'OwnerSoldApartment',
  OwnerRentedForAnotherTenant5A: 'OwnerRentedForAnotherTenant5A',
  OwnerRentedForAnotherTenantOutside: 'OwnerRentedForAnotherTenantOutside',
  TenantRentedAnother5A: 'TenantRentedAnother5A',
  TenantRentedAnotherOutside: 'TenantRentedAnotherOutside',
  OwnerStalled: 'OwnerStalled',
  TenantStalled: 'TenantStalled',
  UnpublishedHouse: 'UnpublishedHouse',
  HouseReserved: 'HouseReserved',
});

export const QA_NEGOTIATION_STATUS_MAP = Object.freeze({
  waiting: 'NEGOTIATION',
  waiting5a: 'NEGOTIATION',
  completed: 'FINISHED',
  cancelled: 'CANCELED',
  terminated: 'FINISHED',
});

export const QA_OFFER_STATUS_MAP = Object.freeze({
  Draft: 'submitted',
  Proposed: 'submitted',
  Accepted: 'accepted',
  Rejected: 'declined',
  Dismiss: 'withdrawn',
});

export const QA_OFFER_TURN_MAP = Object.freeze({
  Owner: 'proprietario',
  Tenant: 'cliente',
});

export const QA_RENT_FLOW_STATUS_MAP = Object.freeze({
  Prospeccao: 'NEW',
  Negociacao: 'NEGOTIATION',
  Documentacao: 'CCV',
  Contrato: 'CCV',
  Cancelled: 'CANCELED',
  Approved: 'FINISHED',
});

export const QA_CONTRACT_STATUS_MAP = Object.freeze({
  Draft: 'draft',
  Sent: 'sent',
  Signing: 'signing',
  Signed: 'signed',
  Active: 'active',
  Terminated: 'terminated',
  Cancelled: 'cancelled',
  Ativo: 'active',
  Cancelado: 'cancelled',
  Minuta: 'draft',
  PreAssinaturas: 'pre_signatures',
});

export const QA_DOCUMENTATION_STATUS_MAP = Object.freeze({
  DOCS_REQUESTED: 'requested',
  DOCS_SENT: 'sent',
  DOCS_APPROVED: 'approved',
  DOCS_REJECTED: 'rejected',
});

export const QA_ACTOR_ROLE_MAP = Object.freeze({
  AGENT: 'corretor',
  OPS: 'interno',
  SUPPLY: 'proprietario',
  DEMAND: 'cliente',
  TENANT_LIVING: 'inquilino',
});

export const QA_USER_TYPE_MAP = Object.freeze({
  landlord: 'LANDLORD',
  tenant: 'TENANT',
  CIQ: 'CIQ',
});

export const QA_AGENT_RATING_DAYS_LIMIT = 15;
export const QA_RATING_DECIMAL_PLACES = 2;

export const QA_DATE_WINDOWS = Object.freeze({
  defaultDatePeriod: 60,
  maxVisitDatePeriod: 90,
  limitOfDays: 30,
  timeSinceVisit: 7,
  timeSinceOfferDraft: 10,
  contractDateFilterMaxDays: 30,
});

export const QA_PHONE_COUNTRY_CODE_BY_LOCALE = Object.freeze({
  'pt-BR': '+55',
  'es-MX': '+52',
});

export const QA_HOUSE_ID_PREFIX = '892700000';
export const QA_HOUSE_ID_LONG_LENGTH = 9;
export const QA_VALID_HOUSE_ID_REGEX = /^(?:892700000)?\d{1,9}$/;
export const QA_POSSIBLE_PHONE_REGEX = /^\+?\d[\d\s().-]{7,}$/;
export const QA_VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const QA_OFFER_TAB_MAP = Object.freeze({
  OFFERS_TAB: 'ongoing',
  DOCUMENTATIONS_TAB: 'documentation',
  CONTRACTS_TAB: 'contract',
});

export const QA_TAB_LABELS_TO_TRACKING_STATUS = Object.freeze({
  OFFERS_TAB: 'offers',
  DOCUMENTATIONS_TAB: 'documentations',
  CONTRACTS_TAB: 'contracts',
});

const normalizeKey = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const normalizeQaBusinessContext = value => {
  const raw = String(value || '').trim();
  const upper = raw.toUpperCase();
  if (Object.values(QA_BUSINESS_CONTEXTS).includes(upper)) return upper;
  return BUSINESS_CONTEXT_ALIASES[normalizeKey(raw)] || null;
};

export const normalizeQaPropertyOfferBusinessContext = value =>
  QA_PROPERTY_OFFER_CONTEXT_BY_REFERENCE[normalizeQaBusinessContext(value)] ||
  null;

export const normalizeQaFinalidade = value =>
  QA_FINALIDADE_BY_REFERENCE_CONTEXT[normalizeQaBusinessContext(value)] || null;

export const normalizeQaHouseType = value =>
  QA_HOUSE_TYPE_BY_REFERENCE[String(value || '').trim()] ||
  HOUSE_TYPE_ALIASES[normalizeKey(value)] ||
  null;

export const normalizeQaAmenityType = value =>
  QA_AMENITY_TYPE_BY_REFERENCE[normalizeKey(value)] || null;

export const normalizeQaHouseOccupancy = value => {
  const raw = String(value || '').trim();
  return (
    QA_HOUSE_OCCUPANCY_BY_REFERENCE[raw.toUpperCase()] ||
    HOUSE_OCCUPANCY_ALIASES[normalizeKey(raw)] ||
    null
  );
};

export const normalizeQaTrackingEvent = value =>
  QA_TRACKING_EVENT_MAP[String(value || '').trim()] ||
  QA_TRACKING_EVENT_MAP[normalizeKey(value)] ||
  String(value || '');
